import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  CircularProgress,
  Snackbar,
  Alert,
  IconButton,
} from '@mui/material';
import { styled, useTheme } from '@mui/system';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { baseUrl } from '../utils/config';
import taskPanelIllustration from '../assets/images/mytask.png';

// Import icons for each category
import AssignmentIcon from '@mui/icons-material/Assignment';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import ApprovalIcon from '@mui/icons-material/Approval';
import RateReviewIcon from '@mui/icons-material/RateReview';
import GavelIcon from '@mui/icons-material/Gavel';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import './MytaskPage.css'; // Ensure this CSS is minimal or provides only necessary global styles

// Assuming a fixed header height
const FIXED_HEADER_HEIGHT_DESKTOP = '64px';
const FIXED_HEADER_HEIGHT_MOBILE = '56px';

// Styled component for individual task items
const TaskItem = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(0),
  borderRadius: '8px', // Changed from 88px, as 88px would make it circular if it was a square, but likely was unintended for a card. If intended, revert.
  backgroundColor: 'transparent',
  boxShadow: 'none',
}));

// Styled component for the inner content box of each task item
const TaskContentBox = styled(Box)(({ theme, category }) => {
  let borderColor;
  switch (category) {
    case 'fill Form':
      borderColor = theme.palette.error.main;
      break;
    case 'review':
      borderColor = theme.palette.warning.main;
      break;
    case 'approve':
      borderColor = theme.palette.info.main;
      break;
    case 'completed':
      borderColor = theme.palette.success.main;
      break;
    case 'submittedForReview':
      borderColor = theme.palette.primary.light;
      break;
    case 'submittedForApproval':
      borderColor = theme.palette.primary.main;
      break;
    default:
      borderColor = theme.palette.grey[300];
  }

  return {
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${borderColor}`,
    borderRadius: '8px',
    padding: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    position: 'relative',
    // Mobile adjustments for padding
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(1.5), // Reduce padding on small screens
    },
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(1), // Further reduce padding on extra-small screens
    },
  };
});

// Styled component for the right-side image
const RightIllustrationContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flex: 2,
  padding: theme.spacing(3),
  maxWidth: '600px',
  height: 'auto',
  // Ensure it's hidden on small screens
  [theme.breakpoints.down('md')]: {
    display: 'none',
  },
}));

// Styled component for category tabs (modified to look like simple text with underline)
const CategoryTab = styled(Typography)(({ theme, selected, categoryKey }) => {
  let selectedTabColor;
  let borderBottomColor;

  if (selected) {
    switch (categoryKey) {
      case 'fill Form':
        selectedTabColor = theme.palette.error.main;
        borderBottomColor = theme.palette.error.main;
        break;
      case 'review':
        selectedTabColor = theme.palette.warning.main;
        borderBottomColor = theme.palette.warning.main;
        break;
      case 'approve':
        selectedTabColor = theme.palette.info.main;
        borderBottomColor = theme.palette.info.main;
        break;
      case 'completed':
        selectedTabColor = theme.palette.success.main;
        borderBottomColor = theme.palette.success.main;
        break;
      case 'submittedForReview':
        selectedTabColor = theme.palette.primary.light;
        borderBottomColor = theme.palette.primary.light;
        break;
      case 'submittedForApproval':
        selectedTabColor = theme.palette.primary.main;
        borderBottomColor = theme.palette.primary.main;
        break;
      default:
        selectedTabColor = theme.palette.primary.main;
        borderBottomColor = theme.palette.primary.main;
    }
  } else {
    selectedTabColor = theme.palette.text.secondary;
    borderBottomColor = 'transparent';
  }

  return {
    padding: theme.spacing(0.5, 1),
    cursor: 'pointer',
    fontWeight: selected ? 'bold' : 'normal',
    color: selectedTabColor,
    borderBottom: `2px solid ${borderBottomColor}`,
    transition: 'border-bottom 0.3s, color 0.3s',
    whiteSpace: 'nowrap', // Crucial to prevent text wrapping and allow horizontal scroll
    flexShrink: 0, // Prevent shrinking when in a flex container with overflow
    '&:hover': {
      color: selected ? selectedTabColor : theme.palette.text.primary,
    },
    // Mobile adjustments for tab font size
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.8rem', // Smaller font size for tabs
      padding: theme.spacing(0.5, 0.75), // Slightly less padding
    },
    [theme.breakpoints.down('xs')]: {
      fontSize: '0.7rem', // Even smaller for extra-small screens
      padding: theme.spacing(0.25, 0.5),
    },
  };
});


const MyTaskPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  const [assignedTasks, setAssignedTasks] = useState([]);
  const [reviewTasks, setReviewTasks] = useState([]);
  const [approvalTasks, setApprovalTasks] = useState([]);
  const [completedTasks, setCompletedTasks] = useState([]);
  const [submittedForReviewTasks, setSubmittedForReviewTasks] = useState([]);
  const [submittedForApprovalTasks, setSubmittedForApprovalTasks] = useState([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [selectedCategory, setSelectedCategory] = useState('fill Form');

  const [currentUserId, setCurrentUserId] = useState(null);

  const _showSnackbar = (message, severity) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  useEffect(() => {
    try {
      const storedUserString = localStorage.getItem('user');
      if (storedUserString) {
        const user = JSON.parse(storedUserString);
        if (user && user._id) {
          setCurrentUserId(user._id);
        } else {
          console.warn("User data in local storage does not contain an '_id' or is malformed.");
          setError("User ID not found in local storage. Please log in again.");
          _showSnackbar("User ID not found. Please log in again.", 'error');
        }
      } else {
        console.warn("No user data found in local storage. User is not logged in.");
        setError("No user data found. Please log in.");
        _showSnackbar("No user data found. Please log in.", 'error');
      }
    } catch (parseError) {
      console.error("Failed to parse user data from local storage:", parseError);
      setError("Error loading user data. Please log in again.");
      _showSnackbar("Error loading user data. Please log in again.", 'error');
    }
  }, []);

  const fetchTasks = async () => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      _showSnackbar("Not authenticated. Please log in.", 'error');
      setLoading(false);
      navigate('/login');
      return;
    }

    if (!currentUserId) {
      console.warn("currentUserId is null when attempting to fetch tasks.");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${baseUrl}/api/scheduled-tasks-user`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        console.log("Fetched data from backend:", data);

        const fetchedAssigned = [];
        const fetchedReview = [];
        const fetchedApproval = [];
        const fetchedCompleted = [];
        const fetchedSubmittedForReview = [];
        const fetchedSubmittedForApproval = [];

        data.forEach(task => {
          const isAssignedToUser = task.assignedToId === currentUserId;
          const isReviewerForUser = task.reviewerId === currentUserId;
          const isApproverForUser = task.approverId === currentUserId;

          if (isAssignedToUser && task.assignmentStatus === 'Pending') {
            fetchedAssigned.push(task);
          }
          if (isReviewerForUser && task.assignmentStatus === 'Submitted' && task.reviewStatus === 'Pending') {
            fetchedReview.push(task);
          }
          if (isApproverForUser && task.assignmentStatus === 'Submitted' && task.reviewStatus === 'Approved' && task.approvalStatus === 'Pending') {
            fetchedApproval.push(task);
          }
          const isTaskFullyApproved = task.assignmentStatus === 'Submitted' && task.reviewStatus === 'Approved' && task.approvalStatus === 'Approved';
          const isTaskRejected = task.assignmentStatus === 'Rejected' || task.reviewStatus === 'Rejected' || task.approvalStatus === 'Rejected';

          if ((isTaskFullyApproved || isTaskRejected) && (isAssignedToUser || isReviewerForUser || isApproverForUser)) {
            fetchedCompleted.push(task);
          }

          if (isAssignedToUser && task.assignmentStatus === 'Submitted' && task.reviewStatus === 'Pending') {
            fetchedSubmittedForReview.push(task);
          }

          const isInApprovalState = task.assignmentStatus === 'Submitted' && task.reviewStatus === 'Approved' && task.approvalStatus === 'Pending';
          if (isInApprovalState && (isAssignedToUser || isReviewerForUser)) {
            fetchedSubmittedForApproval.push(task);
          }
        });

        setAssignedTasks(fetchedAssigned);
        setReviewTasks(fetchedReview);
        setApprovalTasks(fetchedApproval);
        setCompletedTasks(fetchedCompleted);
        setSubmittedForReviewTasks(fetchedSubmittedForReview);
        setSubmittedForApprovalTasks(fetchedSubmittedForApproval);

      } else {
        const errorData = await response.json();
        const errorMessage = errorData.message || `Failed to fetch tasks with status: ${response.status}`;
        throw new Error(errorMessage);
      }
    } catch (err) {
      console.error("Error fetching tasks:", err);
      setError(err.message);
      _showSnackbar(`Error: ${err.message}`, 'error');
      if (err.message.includes('Authentication required') || err.message.includes('Forbidden') || err.message.includes('token')) {
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (currentUserId) {
      fetchTasks();
    }
  }, [currentUserId]);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const getTasksForSelectedCategory = () => {
    let tasksToFilter = [];
    switch (selectedCategory) {
      case 'fill Form':
        tasksToFilter = assignedTasks;
        break;
      case 'submittedForReview':
        tasksToFilter = submittedForReviewTasks;
        break;
      case 'submittedForApproval':
        tasksToFilter = submittedForApprovalTasks;
        break;
      case 'review':
        tasksToFilter = reviewTasks;
        break;
      case 'approve':
        tasksToFilter = approvalTasks;
        break;
      case 'completed':
        tasksToFilter = completedTasks;
        break;
      default:
        tasksToFilter = [];
    }

    const lowerSearchTerm = searchTerm.toLowerCase();
    return tasksToFilter.filter(task =>
      task.formName?.toLowerCase().includes(lowerSearchTerm) ||
      task.scheduledBy?.toLowerCase().includes(lowerSearchTerm) ||
      String(task.assignedToDisplay)?.toLowerCase().includes(lowerSearchTerm) ||
      String(task.reviewerDisplay)?.toLowerCase().includes(lowerSearchTerm) ||
      String(task.approverDisplay)?.toLowerCase().includes(lowerSearchTerm) ||
      String(task.formNumber)?.toLowerCase().includes(lowerSearchTerm)
    );
  };

  const tasksToDisplay = getTasksForSelectedCategory();
  let headingColor;
  switch (selectedCategory) {
    case 'fill Form':
      headingColor = theme.palette.error.main;
      break;
    case 'review':
      headingColor = theme.palette.warning.main;
      break;
    case 'approve':
      headingColor = theme.palette.info.main;
      break;
    case 'completed':
      headingColor = theme.palette.success.main;
      break;
    case 'submittedForReview':
      headingColor = theme.palette.primary.light;
      break;
    case 'submittedForApproval':
      headingColor = theme.palette.primary.main;
      break;
    default:
      headingColor = theme.palette.primary.main;
  }

  const getButtonColor = (category) => {
    switch (category) {
      case 'fill Form':
        return theme.palette.error.main;
      case 'review':
        return theme.palette.warning.main;
      case 'approve':
        return theme.palette.info.main;
      case 'completed':
        return theme.palette.success.main;
      case 'submittedForReview':
        return theme.palette.primary.light;
      case 'submittedForApproval':
        return theme.palette.primary.main;
      default:
        return theme.palette.primary.main;
    }
  };

  return (
    // The outermost div to handle padding-top for fixed header
    <div style={{
      paddingTop: FIXED_HEADER_HEIGHT_DESKTOP,
      minHeight: `calc(100vh - ${FIXED_HEADER_HEIGHT_DESKTOP})`, // Ensure content takes up available space
      overflowY: 'auto', // Allow content to scroll if it exceeds height
      overflowX: 'hidden', // Prevent horizontal scroll
      // Conditional padding-top for mobile
      '@media (max-width: 768px)': {
        paddingTop: FIXED_HEADER_HEIGHT_MOBILE,
        minHeight: `calc(100vh - ${FIXED_HEADER_HEIGHT_MOBILE})`,
      }
    }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          minHeight: '100%', // Changed from calc(100vh - 64px) to 100% to fill parent div
          backgroundColor: theme.palette.background.default,
          alignItems: 'flex-start',
          justifyContent: 'center',
          gap: { xs: theme.spacing(3), md: theme.spacing(20) },
          p: { xs: 2, md: 3 },
        }}
      >
        {/* Left Column: Task List */}
        <Box
          sx={{
            flex: { xs: 'none', md: 3 },
            width: { xs: '100%', md: 'auto' },
            maxWidth: { xs: '100%', md: '800px' },
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: theme.palette.background.default,
            borderRadius: '8px',
            p: 0,
            // Min height for task list container, adjust based on your needs
            // It allows the box to stretch even if there are few tasks
            minHeight: { xs: 'calc(100vh - 180px)', sm: 'calc(100vh - 120px)', md: '600px' }, // More responsive minHeight for mobile
            boxSizing: 'border-box', // Include padding in width/height
          }}
        >
          {/* Fixed Header Section (for tasks, like My Tasks title, tabs, search) */}
          <Box
            sx={{
              p: { xs: 1.5, sm: 2 },
              pb: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: { xs: 1, sm: 2 },
              width: '100%',
              zIndex: 2, // Ensure this header is above the scrollable content
              backgroundColor: theme.palette.background.default,
              boxShadow: theme.shadows[1], // Subtle shadow for fixed header look
              position: 'sticky', // Make it sticky
              top: 0, // Stick to the top of its parent (the left column box)
            }}
          >
            {/* Main Title */}
            <Typography
              variant="h4"
              sx={{
                fontWeight: 'bold',
                color: theme.palette.text.primary,
                mb: { xs: 1.5, sm: 3 },
                fontSize: { xs: '1.8rem', sm: '2.125rem' },
              }}
            >
              My Tasks
            </Typography>

            {/* Tabs Container */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-start',
                width: '100%',
                mb: { xs: 1.5, sm: 2 },
                overflowX: 'auto', // Allows tabs to scroll horizontally
                flexShrink: 0, // Prevent tabs container from shrinking
                '&::-webkit-scrollbar': { display: 'none' }, // Hide scrollbar for tabs
                '-ms-overflow-style': 'none',
                'scrollbar-width': 'none',
                gap: { xs: theme.spacing(1), sm: theme.spacing(2) },
              }}
            >
              {/* Individual Tabs */}
              <CategoryTab
                selected={selectedCategory === 'fill Form'}
                onClick={() => setSelectedCategory('fill Form')}
                categoryKey="fill Form"
                count={assignedTasks.length}
              >
                Fill Form ({assignedTasks.length})
              </CategoryTab>
              <CategoryTab
                selected={selectedCategory === 'review'}
                onClick={() => setSelectedCategory('review')}
                categoryKey="review"
                count={reviewTasks.length}
              >
                Review ({reviewTasks.length})
              </CategoryTab>
              <CategoryTab
                selected={selectedCategory === 'approve'}
                onClick={() => setSelectedCategory('approve')}
                categoryKey="approve"
                count={approvalTasks.length}
              >
                Approve ({approvalTasks.length})
              </CategoryTab>
              <CategoryTab
                selected={selectedCategory === 'submittedForReview'}
                onClick={() => setSelectedCategory('submittedForReview')}
                categoryKey="submittedForReview"
                count={submittedForReviewTasks.length}
              >
                In Review ({submittedForReviewTasks.length})
              </CategoryTab>
              <CategoryTab
                selected={selectedCategory === 'submittedForApproval'}
                onClick={() => setSelectedCategory('submittedForApproval')}
                categoryKey="submittedForApproval"
                count={submittedForApprovalTasks.length}
              >
                In Approval ({submittedForApprovalTasks.length})
              </CategoryTab>
              <CategoryTab
                selected={selectedCategory === 'completed'}
                onClick={() => setSelectedCategory('completed')}
                categoryKey="completed"
                count={completedTasks.length}
              >
                Completed ({completedTasks.length})
              </CategoryTab>
            </Box>

            {/* Search Bar */}
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search by form name, number, or assigned/reviewer/approver..."
              value={searchTerm}
              onChange={handleSearchChange}
              sx={{
                mb: { xs: 1.5, sm: 2 },
                backgroundColor: theme.palette.background.paper,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  borderColor: theme.palette.grey[300],
                  '&.Mui-focused fieldset': {
                    borderColor: theme.palette.primary.main,
                  },
                },
              }}
              InputProps={{
                startAdornment: (
                  <Typography sx={{ mr: 1, color: theme.palette.text.secondary }}>🔍</Typography>
                ),
                sx: { height: { xs: '40px', sm: '48px' } }
              }}
            />
          </Box>

          {/* Scrollable Task List Content Section */}
          <Box
            sx={{
              p: { xs: 1.5, sm: 2 },
              pt: 0, // No top padding here as it's part of the fixed header area
              flexGrow: 1,
              overflowY: 'auto', // This creates the scrollbar for the task list
              pr: { xs: 0.5, sm: 1 },
              // Max height for the scrollable area
              // This calc needs to be precise: total container height - (fixed header height + its bottom margin)
              maxHeight: {
                xs: `calc(100vh - ${FIXED_HEADER_HEIGHT_MOBILE} - 150px)`, // Estimate header+tabs+search
                sm: `calc(100vh - ${FIXED_HEADER_HEIGHT_MOBILE} - 150px)`,
                md: `calc(100vh - ${FIXED_HEADER_HEIGHT_DESKTOP} - 150px)` // Adjust 150px to fit title, tabs, search on desktop
              },
              boxSizing: 'border-box', // Include padding in max-height
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: 'bold',
                mb: { xs: 1, sm: 1 },
                color: headingColor,
                fontSize: { xs: '1.1rem', sm: '1.25rem' },
              }}
            >
              {selectedCategory === 'submittedForReview' ? 'My Submissions (In Review)' :
                selectedCategory === 'submittedForApproval' ? 'My Submissions (In Approval)' :
                  `${selectedCategory.charAt(0).toUpperCase() + selectedCategory.slice(1)} Tasks`}
            </Typography>

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : error ? (
              <Alert severity="error">{error}</Alert>
            ) : tasksToDisplay.length === 0 ? (
              <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', py: 4, fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                {searchTerm ? 'No matching tasks found in this category.' :
                  `No tasks found in the ${selectedCategory === 'submittedForReview' ? 'My Submissions (In Review)' :
                                        selectedCategory === 'submittedForApproval' ? 'My Submissions (In Approval)' :
                                        selectedCategory} category.`}
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 1.5, sm: 2 }, width: '100%' }}>
                {tasksToDisplay.map((task) => {
                  const actualButtonText = (selectedCategory === 'submittedForReview' || selectedCategory === 'submittedForApproval' || selectedCategory === 'completed') ? 'View' : 'Edit';
                  const buttonBgColor = getButtonColor(selectedCategory);

                  return (
                    <TaskItem key={task._id} elevation={0}>
                      <TaskContentBox category={selectedCategory}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 } }}>
                          {(() => {
                            let IconComponent = null;
                            let iconColor = theme.palette.text.secondary;

                            switch (selectedCategory) {
                              case 'fill Form': IconComponent = AssignmentIcon; iconColor = theme.palette.error.main; break;
                              case 'submittedForReview': IconComponent = PendingActionsIcon; iconColor = theme.palette.primary.light; break;
                              case 'submittedForApproval': IconComponent = ApprovalIcon; iconColor = theme.palette.secondary.main; break;
                              case 'review': IconComponent = RateReviewIcon; iconColor = theme.palette.warning.main; break;
                              case 'approve': IconComponent = GavelIcon; iconColor = theme.palette.info.main; break;
                              case 'completed': IconComponent = CheckCircleOutlineIcon; iconColor = theme.palette.success.main; break;
                              default: IconComponent = AssignmentIcon; iconColor = theme.palette.grey[500];
                            }
                            return IconComponent ? (
                              <IconComponent sx={{ color: iconColor, fontSize: { xs: '1rem', sm: '1.2rem' } }} />
                            ) : null;
                          })()}
                          <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.text.primary, mb: 0.5, fontSize: { xs: '0.95rem', sm: '1.1rem' } }}>
                            {task.formName} - {task.formNumber || ''}
                          </Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                          Scheduled by: {task.scheduledBy}
                        </Typography>

                        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, flexWrap: 'wrap', gap: { xs: 0.5, sm: 1 }, mb: 1 }}>
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                            AssignedTo: <Typography component="span" variant="body2" sx={{ fontWeight: 'bold', fontSize: 'inherit' }}>
                              {task.assignedToDisplay || 'N/A'}
                            </Typography>
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                            | Reviewer: <Typography component="span" variant="body2" sx={{ fontWeight: 'bold', fontSize: 'inherit' }}>
                              {task.reviewerDisplay || 'N/A'}
                            </Typography>
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                            | Approver: <Typography component="span" variant="body2" sx={{ fontWeight: 'bold', fontSize: 'inherit' }}>
                              {task.approverDisplay || 'N/A'}
                            </Typography>
                          </Typography>
                        </Box>

                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                          Schedule Date: {format(new Date(task.scheduledAt), 'dd/MM/yyyy')} Due Date: {format(new Date(task.dueDate), 'dd/MM/yyyy')}
                        </Typography>

                        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, flexWrap: 'wrap', gap: { xs: 0.5, sm: 1 }, mb: 1 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                            Assignee: <Typography component="span" variant="caption" sx={{ fontWeight: 'bold', color: task.assignmentStatus === 'Pending' ? theme.palette.error.dark : theme.palette.success.dark, fontSize: 'inherit' }}>
                              {task.assignmentStatus || 'N/A'}
                            </Typography>
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                            Reviewer: <Typography component="span" variant="caption" sx={{ fontWeight: 'bold', color: task.reviewStatus === 'Pending' ? theme.palette.warning.dark : theme.palette.success.dark, fontSize: 'inherit' }}>
                              {task.reviewStatus === 'Approved' ? 'Reviewed' : task.reviewStatus || 'N/A'}
                            </Typography>
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                            Approver: <Typography component="span" variant="caption" sx={{ fontWeight: 'bold', color: task.approvalStatus === 'Pending' ? theme.palette.info.dark : theme.palette.success.dark, fontSize: 'inherit' }}>
                              {task.approvalStatus || 'N/A'}
                            </Typography>
                          </Typography>
                        </Box>

                        <Button
                          variant="contained"
                          size="small"
                          sx={{
                            position: 'absolute',
                            top: theme.spacing(2),
                            right: theme.spacing(2),
                            minWidth: 'auto',
                            height: '35px',
                            width: '70px',
                            fontSize: '0.8rem',
                            borderRadius: '4px',
                            textTransform: 'uppercase',
                            backgroundColor: buttonBgColor,
                            '&:hover': {
                              backgroundColor: buttonBgColor,
                              opacity: 0.9,
                            },
                            color: theme.palette.common.white,
                            [theme.breakpoints.down('sm')]: {
                              position: 'static',
                              marginTop: theme.spacing(1),
                              alignSelf: 'flex-end',
                              width: 'auto',
                              padding: theme.spacing(0.5, 1),
                              fontSize: '0.7rem',
                              height: 'auto',
                            },
                          }}
                          onClick={() => navigate(`/dashboard/task/${task._id}`)}
                        >
                          {actualButtonText}
                        </Button>
                      </TaskContentBox>
                    </TaskItem>
                  )
                })}
              </Box>
            )}
          </Box>
        </Box>

        {/* Right Column: Illustration */}
        <RightIllustrationContainer>
          <Box
            component="img"
            src={taskPanelIllustration}
            alt="Task Panel Illustration"
            sx={{
              maxHeight: '80vh',
              width: 'auto',
              objectFit: 'contain',
            }}
          />
        </RightIllustrationContainer>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        >
          <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Box>
    </div>
  );
};

export default MyTaskPage;